import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import { CareItem } from 'src/replicant/care/care.ruleset';
import StateObserver from 'src/StateObserver';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import * as Care from 'src/replicant/care';
import { ImageScaleViewOpts } from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { trackPinchEvent } from 'src/lib/analytics/events';
import { Feature } from 'src/replicant/analytics';
import { mod } from 'src/replicant/utils';
import TutorialHand from '../components/shared/TutorialHand';
import { getTutorialController } from '../tutorial/TutorialController';
import { registerHandTarget } from '../tutorial/services/TutorialHandService';
import { puzzleCoinsPinch } from '../puzzlecoins/puzzlecoins.sequence';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result?: {
  accepted: boolean;
  multiplier: number;
}) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupConfirmCareItem extends PopupBasic {
  private close: CloseHandler;
  private container: View;
  private cost: LangBitmapFontTextView;
  private balance: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;
  private _multiplier = 1;
  private maxMultiplier = 1;
  private hand: TutorialHand | undefined;

  private get multiplier() {
    return this._multiplier;
  }

  private set multiplier(value: number) {
    this._multiplier = mod(value - 1, this.maxMultiplier) + 1;
  }

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      ...ui.popup,
      tracking: { feature: 'care', $subFeature: 'popup-confirmcareitem' },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      ...ui.message,
      width: this.box.style.width - 2 * ui.message.x,
    });

    this.box.addSubview(
      new ImageScaleView({
        ...ui.underline,
        width: this.message.style.width,
      }),
    );

    this.container = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    const costRow = this.container.addSubview(new ImageSliceView(ui.costRow));

    costRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('careItem.pointsRequired'),
      }),
    );

    this.cost = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: costRow,
    });

    costRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        x: costRow.style.width - ui.currency.width,
      }),
    );

    const balanceRow = this.container.addSubview(
      new ImageSliceView(ui.balanceRow),
    );

    balanceRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('careItem.pointsRemaining'),
      }),
    );

    this.balance = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: balanceRow,
    });

    balanceRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        x: balanceRow.style.width - ui.currency.width,
      }),
    );

    this.container.addSubview(
      new ButtonScaleViewWithText({
        ...ui.backButton,
        localeText: () => i18n('basic.back'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close({ accepted: false, multiplier: this.multiplier });
        },
        tracking: {
          feature: Feature.CARE._,
          $subFeature: 'popup-confirmcareitem-back',
        },
      }),
    );

    this.okButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.container,
      localeText: () => i18n('basic.okay'),
      tracking: {
        feature: Feature.CARE._,
        $subFeature: 'popup-confirmcareitem-ok',
      },
    });

    registerHandTarget('confirmButton', this.okButton);
  }

  protected getAssetsGroup() {
    return new AssetGroup([]);
  }

  public init({ careItemId }: { careItemId: CareItem['id'] }) {
    const careItem = Care.ruleset.careItems[careItemId];
    super.init({
      title: i18n('careItem.confirmTitle'),
      message: i18n('careItem.confirmMessage'),
    });

    this.box.addSubview(new CareItemPanel({ careItem }));

    this.updateCostAndRemaining(careItem);

    this.okButton.onClick = async () => {
      sounds.sfx.play('ok');

      const canPurchase = this.canPurchase(careItem);
      if (!canPurchase) {
        this.fadeOut();
        this.trackCareItemPinchEvent(careItemId);
        await puzzleCoinsPinch();
      }

      this.close({ accepted: canPurchase, multiplier: this.multiplier });
    };

    if (!this.canPurchase(careItem)) {
      this.balance.updateOpts({ color: 'red' });
    }

    void getTutorialController().handleEvent('careConfirmPopupOpened');
  }

  /**
   * Check if player can purchase care item
   */
  private canPurchase(careItem: CareItem) {
    return this.getBalanceAfterPurchase(careItem) >= 0;
  }

  /**
   * Get balance after purchase, can be negative
   */
  private getBalanceAfterPurchase(careItem: CareItem) {
    return (
      StateObserver.getState().user.inventory.points -
      careItem.price * this.multiplier
    );
  }

  /**
   * Send track care items
   */
  private trackCareItemPinchEvent(careItemId: CareItem['id']) {
    const careItem = Care.ruleset.careItems[careItemId];
    const type = careItem.type;
    if (type === 'invitation') {
      trackPinchEvent({
        resource: 'bPoints',
        trigger: 'duo',
        item_id: careItemId,
      });
    } else {
      trackPinchEvent({
        resource: 'bPoints',
        trigger: `careAction_${type}`,
        item_id: careItemId,
      });
    }
  }

  private updateCostAndRemaining(careItem: CareItem) {
    this.cost.localeText = () => parseAmount(this.getCost(careItem));

    const balance = this.getBalanceAfterPurchase(careItem);
    this.balance.localeText = () => parseAmount(balance);
  }

  private getCost(careItem: CareItem) {
    return careItem.price * this.multiplier;
  }
}

type CareItemPanelOpts = Omit<
  ImageScaleViewOpts,
  'image' | 'width' | 'height'
> & {
  careItem: CareItem;
};

class CareItemPanel extends ImageScaleView {
  constructor({ careItem, ...opts }: CareItemPanelOpts) {
    super({
      ...opts,
      ...ui.careItemPanel,
    });

    // icon
    this.addSubview(
      new ImageScaleView({
        scaleMethod: 'contain',
        x: 20,
        y: 20,
        width: 156,
        height: 137,
        image: ui.careItemIcons[careItem.item],
      }),
    );

    // name
    const name = careItem[`name_${getLocaleSuffix()}`];
    this.addSubview(
      new LangBitmapFontTextView({
        font: 'Body',
        color: '#9A7171',
        y: 25,
        size: 30,
        x: 190,
        height: 39,
        width: 283,
        localeText: () => name.replace(/\n/g, ''), // Strip newlines
        verticalAlign: 'bottom',
      }),
    );

    // description
    this.addSubview(
      new LangBitmapFontTextView({
        size: 25,
        x: 190,
        y: 85,
        width: 283,
        height: 64,
        font: 'Body',
        color: '#9A7171',
        localeText: () => careItem[`desc_${getLocaleSuffix()}`],
        wordWrap: StateObserver.getState().ui.locale === 'en',
      }),
    );
  }
}

const getLocaleSuffix = () =>
  StateObserver.getState().ui.locale === 'en' ? 'en' : 'jp';
